/**
 * Mixpanel en la tienda (task-1635).
 *
 * El navegador manda los eventos de navegación e intención: página, producto,
 * carrito e inicio de checkout. La compra, el pago fallido y el despacho los
 * manda el backend (Laravel) cuando el pago o Drivin los confirman, para no
 * depender de que el cliente vuelva a la tienda. Ver MIXPANEL_INTEGRATION.md.
 *
 * - Sin GATSBY_MIXPANEL_TOKEN no se hace nada (local, o un ambiente sin
 *   proyecto configurado).
 * - Nada de esto puede romper la tienda: cada llamada va protegida.
 * - Un admin que entra como cliente (impersonación) no manda eventos. Sobre todo
 *   no hace identify(): eso uniría el navegador del admin con el perfil del
 *   cliente para siempre.
 *
 * Se carga la versión "core" del SDK, sin el grabador de sesiones
 * (~375 KB menos de bundle). La grabación de sesiones no se usa.
 */
import mixpanel from 'mixpanel-browser/src/loaders/loader-module-core';
import { detectPageType } from '../hooks/usePageTracking';

const TOKEN = process.env.GATSBY_MIXPANEL_TOKEN;

let iniciado = false;

const impersonando = () => {
  try {
    return localStorage.getItem('is_impersonating') === 'true';
  } catch (e) {
    return false;
  }
};

const activo = () => iniciado && !impersonando();

export function initMixpanel() {
  if (iniciado || !TOKEN || typeof window === 'undefined') return;
  try {
    mixpanel.init(TOKEN, {
      // Recomendación de Mixpanel para web: el id del visitante sobrevive a
      // recargas y no viaja en cada request como una cookie.
      persistence: 'localStorage',
      // La vista de página la manda onRouteUpdate, con el tipo de página.
      track_pageview: false,
      debug: process.env.NODE_ENV === 'development',
    });
    iniciado = true;
  } catch (e) {
    // La analítica nunca rompe la tienda.
  }
}

export function track(evento, propiedades = {}) {
  if (!activo()) return;
  try {
    mixpanel.track(evento, propiedades);
  } catch (e) {
    // idem
  }
}

/**
 * Une la navegación anónima de este navegador con el usuario. Se usa el id de
 * nuestra base (no el correo): es el mismo distinct_id que usa el backend en
 * la compra, y un correo puede cambiar.
 */
export function identifyUser(user) {
  if (!activo() || !user || !user.id) return;
  try {
    const id = String(user.id);
    // Se llama siempre, aunque el id ya sea el mismo: así lo pide Mixpanel en cada
    // carga con sesión. Con el mismo id no genera otro $identify, pero marca la
    // página como identificada y suelta los cambios de perfil en cola. Si no, el
    // people.set de abajo quedaba esperando un identify que nunca llegaba y un
    // cambio de nombre o correo no se enviaba hasta el próximo login.
    mixpanel.identify(id);
    const nombre = [user.name, user.last_name].filter(Boolean).join(' ');
    mixpanel.people.set({
      ...(user.email ? { $email: user.email } : {}),
      ...(nombre ? { $name: nombre } : {}),
      user_id: user.id,
    });
  } catch (e) {
    // idem
  }
}

// La sesión puede llegar como el usuario o envuelta en { user } (según el endpoint).
const usuarioDe = (datos) => (datos && datos.user && datos.user.id ? datos.user : datos);

/**
 * Inicio de sesión (método 'email' o 'google'). Se manda recién después de
 * identify, para que quede en el perfil del usuario y no en el del visitante.
 * Sólo en el momento del login, no al restaurar una sesión al recargar.
 */
export function trackLogin(datosUsuario, metodo) {
  if (!activo()) return;
  identifyUser(usuarioDe(datosUsuario));
  track('Logged In', { method: metodo });
}

/** Registro de una cuenta nueva (método 'email' o 'google'). */
export function trackSignUp(datosUsuario, metodo) {
  if (!activo()) return;
  identifyUser(usuarioDe(datosUsuario));
  track('Signed Up', { method: metodo });
}

/** Al cerrar sesión: el próximo que use este navegador empieza como anónimo. */
export function resetMixpanel() {
  if (!iniciado) return;
  try {
    mixpanel.reset();
  } catch (e) {
    // idem
  }
}

/**
 * Identificador de Mixpanel de este navegador, para mandarlo al crear el
 * pedido. Con él, la compra que manda el backend queda en el mismo perfil que
 * navegó el sitio. Anónimo es "$device:<id>"; con sesión, el id del usuario.
 */
export function getDistinctId() {
  if (!activo()) return null;
  try {
    return mixpanel.get_distinct_id() || null;
  } catch (e) {
    return null;
  }
}

export function trackPageViewed(pathname) {
  if (!activo()) return;
  const { page_type, entity_slug } = detectPageType(pathname);
  track('Page Viewed', {
    url: pathname,
    page_type,
    ...(entity_slug ? { entity_slug } : {}),
    title: typeof document !== 'undefined' ? document.title : undefined,
  });
}

const precio = (producto) => {
  const valor = Number(producto && (Number(producto.sale_price) > 0 ? producto.sale_price : producto.price));
  return Number.isFinite(valor) ? valor : undefined;
};

const nombreDe = (valor) => {
  const nombre = valor && typeof valor === 'object' ? valor.name : valor;
  return typeof nombre === 'string' && nombre.trim() !== '' ? nombre.trim() : undefined;
};

/**
 * Datos del producto para todos los eventos de producto (revisión del cliente):
 * brand, distributor, tags (lista de nombres), category y subcategory, con los
 * mismos nombres y formato que cada producto de Purchase Completed.
 *
 * Los arma el backend (clave `analytics` del catálogo y del carrito con sesión,
 * App\Support\ProductAnalytics). Si un producto no la trae (p. ej. un carrito
 * guardado antes de este cambio), se usa lo que tenga: tags y categoría. Un dato
 * que no existe no se manda; tags va siempre, aunque sea vacía.
 */
function propiedadesProducto(producto) {
  const datos = (producto && producto.analytics) || {};
  const tags = Array.isArray(datos.tags)
    ? datos.tags
    : ((producto && producto.tags) || []).map(nombreDe).filter(Boolean);
  const categorias = (producto && producto.categories) || [];
  const propiedades = {
    brand: nombreDe(datos.brand),
    distributor: nombreDe(datos.distributor),
    tags,
    category: nombreDe(datos.category) || nombreDe(categorias[0]),
    subcategory: nombreDe(datos.subcategory),
  };
  Object.keys(propiedades).forEach((clave) => propiedades[clave] === undefined && delete propiedades[clave]);
  return propiedades;
}

/** ¿Hay sesión iniciada? Separa en el embudo el carrito con y sin sesión. */
function conSesion() {
  try {
    return !!localStorage.getItem('auth_token');
  } catch (e) {
    return false;
  }
}

/** Ficha de producto. `producto` es el de templates/Product.js. */
export function trackProductViewed(producto) {
  if (!producto) return;
  track('Product Viewed', {
    product_id: producto.internal_id,
    sku: producto.sku,
    name: producto.name,
    price: precio(producto),
    in_stock: producto.stock_status === 'instock',
    ...propiedadesProducto(producto),
  });
}

/**
 * Desde dónde se agregó un producto al carrito (add_source), según la página en
 * que está el cliente: search, category, brand, distributor, product_page o home
 * ("Agregar los N" de la ficha también cuenta como product_page). Los demás lugares con botón de agregar: tag (etiquetas),
 * launch_landing (lanzamientos) y reorder (repetir una compra en Mis compras);
 * cualquier otra página, other.
 *
 * Los resultados del buscador en vivo se dibujan encima de cualquier página sin
 * cambiar la URL: por eso el buscador pasa 'search' explícitamente.
 */
export function addSourceFromPage(pathname) {
  const ruta = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  if (ruta === '/' || ruta === '') return 'home';
  if (ruta.startsWith('/buscar')) return 'search';
  if (ruta.startsWith('/categoria-producto/')) return 'category';
  if (ruta.startsWith('/marca/')) return 'brand';
  if (ruta.startsWith('/partners/')) return 'distributor';
  if (ruta.startsWith('/producto/')) return 'product_page';
  if (ruta.startsWith('/etiqueta-producto/')) return 'tag';
  if (ruta.startsWith('/lanzamiento/')) return 'launch_landing';
  if (ruta.startsWith('/my-shoppings')) return 'reorder';
  return 'other';
}

/**
 * Producto agregado al carrito. `producto` es el payload de addToCart.
 * `addSource`: ver addSourceFromPage. `source`: 'add_multiple' en "Agregar los N".
 */
export function trackAddedToCart(producto, { addSource, source } = {}) {
  if (!producto) return;
  track('Product Added to Cart', {
    product_id: producto.product_id,
    sku: producto.sku,
    name: producto.name,
    price: precio(producto),
    quantity: Number(producto.quantity) || 1,
    logged_in: conSesion(),
    ...propiedadesProducto(producto),
    add_source: addSource || addSourceFromPage(),
    ...(source ? { source } : {}),
  });
}

/**
 * Búsqueda terminada (Enter o una pausa al escribir): `search_term` es lo que
 * escribió el cliente y `results_count` el total de resultados (0 si no hubo).
 * `fuzzy_match` = true cuando no hubo coincidencias exactas y el buscador
 * mostró productos parecidos (un error de tipeo, por ejemplo): esos resultados
 * cuentan en results_count.
 */
export function trackSearchPerformed(termino, cantidad, parecidos) {
  track('Search Performed', {
    search_term: termino,
    results_count: Number(cantidad) || 0,
    fuzzy_match: !!parecidos,
  });
}

/** Entrada al checkout, con el carrito del contexto (state.cart). */
export function trackCheckoutStarted(carrito, total) {
  const items = Array.isArray(carrito) ? carrito : [];
  track('Checkout Started', {
    cart_total: Number(total) || 0,
    items_count: items.length,
    units: items.reduce((suma, item) => suma + (Number(item.quantity) || 0), 0),
    logged_in: conSesion(),
    // Cada producto con el mismo formato que Product Added to Cart y que cada
    // producto de Purchase Completed.
    products: items.map((item) => ({
      product_id: item.product_id,
      sku: item.sku,
      name: item.name,
      quantity: Number(item.quantity) || 1,
      price: precio(item),
      ...propiedadesProducto(item),
    })),
    content_ids: items.map((item) => item.product_id),
  });
}
